import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import {
  listClients, createClient, updateClient, deleteClient, listKeys, issueKey, revokeKey, getUsage, getClientUsage, errorMessage,
  SCOPES, Scope, ClientStatus, ClientInput, EnrichmentClient, ApiKeyRecord, NewApiKey, ClientUsage, UsageDay,
} from '../../services/enrichmentApi';
import { BigSkyEnrichmentShell } from '../Vertebrae/BigSkyEnrichmentShell';
import { OWNED_ATTRIBUTE_CATALOG } from '../Vertebrae/ownedAttributeCatalog';

// ---------------------------------------------------------------------------
// Enrichment API: super_admin board for the BigSky Enrichment API's clients
// (enrich.databody.app), their API keys and usage. Create / edit / suspend /
// delete clients; issue and revoke keys; usage per period and per day. A new
// key is shown once. Clients with usage can't be deleted: suspend them.
// ---------------------------------------------------------------------------

const th: React.CSSProperties = {
  textAlign: 'left', padding: '10px 12px', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4,
  color: '#e5e7eb', borderBottom: '1px solid #2d3142', whiteSpace: 'nowrap', background: '#1a1d27',
};
const td: React.CSSProperties = { padding: '10px 12px', fontSize: 13, color: '#1f2937', background: '#ffffff', borderBottom: '1px solid #e5e7eb', verticalAlign: 'top' };
const num: React.CSSProperties = { ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' };
const label: React.CSSProperties = { display: 'block', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4, color: '#4b5563', marginBottom: 4 };
const input: React.CSSProperties = { width: '100%', padding: '8px 10px', fontSize: 13, color: '#1f2937', background: '#ffffff', border: '1px solid #d1d5db', borderRadius: 6, boxSizing: 'border-box' };
const field: React.CSSProperties = { marginBottom: 14 };
const hint: React.CSSProperties = { fontSize: 11, color: '#6b7280', marginTop: 4 };
const overlay: React.CSSProperties = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: '40px 16px', zIndex: 1000 };
const modal = (w: number): React.CSSProperties => ({ width: '100%', maxWidth: w, background: '#ffffff', color: '#1f2937', border: '1px solid #e5e7eb', borderRadius: 10, padding: 24, boxShadow: '0 8px 24px rgba(0,0,0,0.12)' });
const mono: React.CSSProperties = { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 12 };
const small: React.CSSProperties = { padding: '2px 8px', fontSize: 12, marginRight: 4 };

const SCOPE_HELP: Record<Scope, string> = {
  enrich: 'Customer API (/v1/enrich)',
  'graph:read': 'Read the identity graph (/internal)',
  'graph:write': 'Write to the identity graph (/internal)',
  admin: 'Manage clients and keys (this page)',
};

const fmtDate = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleString() : '—');
function ago(iso: string | null | undefined): string {
  if (!iso) { return 'never'; }
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 90) { return `${Math.max(1, Math.round(s))}s ago`; }
  if (s < 5400) { return `${Math.round(s / 60)}m ago`; }
  if (s < 172800) { return `${Math.round(s / 3600)}h ago`; }
  return `${Math.round(s / 86400)}d ago`;
}

function Badge({ children, color }: { children: React.ReactNode; color: string }) {
  return (
    <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 700, color, background: `${color}22`, border: `1px solid ${color}55`, whiteSpace: 'nowrap', marginRight: 4 }}>
      {children}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Client editor (create + edit)
// ---------------------------------------------------------------------------

interface Draft {
  name: string; status: ClientStatus; rate: string; scopes: Scope[]; liveLookup: boolean; mcpEnabled: boolean;
  allAttributes: boolean; attributes: string[]; createKey: boolean; keyLabel: string;
}

const toDraft = (c: EnrichmentClient | null): Draft => ({
  name: c?.name ?? '',
  status: c?.status ?? 'active',
  rate: String(c?.rate_limit_per_minute ?? 600),
  scopes: c?.scopes ?? ['enrich'],
  liveLookup: c?.live_lookup ?? false,
  mcpEnabled: c?.mcp_enabled ?? false,
  allAttributes: !c?.allowed_attributes,
  attributes: c?.allowed_attributes ?? [],
  createKey: true,
  keyLabel: '',
});

function ClientEditor({ client, onClose, onSaved }: {
  client: EnrichmentClient | null;
  onClose: () => void;
  onSaved: (newKey?: { clientName: string; key: NewApiKey }) => void;
}) {
  const { getToken } = useAuth();
  const [d, setD] = useState<Draft>(() => toDraft(client));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setD(prev => ({ ...prev, [k]: v }));

  const toggleScope = (s: Scope) =>
    set('scopes', d.scopes.includes(s) ? d.scopes.filter(x => x !== s) : SCOPES.filter(x => x === s || d.scopes.includes(x)));

  const save = async () => {
    setError('');
    const rate = parseInt(d.rate, 10);
    const attributes = [...new Set(d.attributes)];
    if (!d.name.trim()) { setError('Name is required.'); return; }
    if (!(rate >= 1 && rate <= 1_000_000)) { setError('Rate limit must be between 1 and 1,000,000 per minute.'); return; }
    if (!d.scopes.length) { setError('Pick at least one scope.'); return; }
    if (!d.allAttributes && !attributes.length) { setError('Pick at least one attribute, or allow all attributes.'); return; }

    const body: ClientInput = {
      name: d.name.trim(),
      status: d.status,
      rate_limit_per_minute: rate,
      allowed_attributes: d.allAttributes ? null : attributes,
      scopes: d.scopes,
      live_lookup: d.liveLookup,
      mcp_enabled: d.mcpEnabled,
    };
    setSaving(true);
    try {
      const token = await getToken();
      if (!token) { setError('Your session expired. Please sign in again.'); return; }
      if (client) {
        await updateClient(token, client.id, body);
        onSaved();
      } else {
        const r = await createClient(token, { ...body, create_key: d.createKey, ...(d.createKey && d.keyLabel.trim() ? { key_label: d.keyLabel.trim() } : {}) });
        onSaved(r.key ? { clientName: r.client.name, key: r.key } : undefined);
      }
    } catch (e: any) {
      setError(errorMessage(e, 'Save failed'));
    } finally { setSaving(false); }
  };

  return (
    <div style={overlay} onClick={onClose}>
      <div style={modal(760)} onClick={e => e.stopPropagation()}>
        <h2 style={{ margin: '0 0 18px', fontSize: 18 }}>{client ? `Edit client: ${client.name}` : 'New client'}</h2>

        <div style={field}>
          <label style={label}>Name</label>
          <input style={input} value={d.name} maxLength={200} onChange={e => set('name', e.target.value)} placeholder="Company name" autoFocus />
          <div style={hint}>Must be unique.</div>
        </div>

        <div style={{ display: 'flex', gap: 12 }}>
          <div style={{ ...field, flex: 1 }}>
            <label style={label}>Status</label>
            <select style={input} value={d.status} onChange={e => set('status', e.target.value as ClientStatus)}>
              <option value="active">Active</option>
              <option value="suspended">Suspended (all keys stop working)</option>
            </select>
          </div>
          <div style={{ ...field, flex: 1 }}>
            <label style={label}>Rate limit / minute</label>
            <input style={input} type="number" min={1} max={1000000} value={d.rate} onChange={e => set('rate', e.target.value)} />
            <div style={hint}>Across all its keys.</div>
          </div>
        </div>

        <div style={field}>
          <label style={label}>Scopes</label>
          {SCOPES.map(s => (
            <label key={s} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginBottom: 4, cursor: 'pointer' }}>
              <input type="checkbox" checked={d.scopes.includes(s)} onChange={() => toggleScope(s)} />
              <span style={mono}>{s}</span><span style={{ color: '#6b7280', fontSize: 12 }}>· {SCOPE_HELP[s]}</span>
            </label>
          ))}
        </div>

        <div style={field}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
            <input type="checkbox" checked={d.liveLookup} onChange={e => set('liveLookup', e.target.checked)} />
            Live lookups
          </label>
          <div style={hint}>On a miss, /v1/enrich may ask the live provider during the call (paid per match).</div>
        </div>

        <div style={field}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
            <input type="checkbox" checked={d.mcpEnabled} onChange={e => set('mcpEnabled', e.target.checked)} />
            MCP access
          </label>
          <div style={hint}>Its keys may also call the API through the MCP server (AI agents). Scopes, rate limit and attributes still apply.</div>
        </div>

        <div style={field}>
          <label style={label}>Attributes it may receive</label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginBottom: 6, cursor: 'pointer' }}>
            <input type="checkbox" checked={d.allAttributes} onChange={e => set('allAttributes', e.target.checked)} />
            All attributes
          </label>
          {!d.allAttributes && (
            // Same picker as a data body's Identity Enrichment step (same canonical catalog as the API).
            <BigSkyEnrichmentShell
              selected={d.attributes}
              onChange={next => set('attributes', next)}
              catalog={OWNED_ATTRIBUTE_CATALOG}
              showProvisionalNote={false}
              showBillingNote={false}
            />
          )}
        </div>

        {!client && (
          <div style={{ ...field, borderTop: '1px solid #e5e7eb', paddingTop: 14 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
              <input type="checkbox" checked={d.createKey} onChange={e => set('createKey', e.target.checked)} />
              Issue a first API key
            </label>
            {d.createKey && (
              <input style={{ ...input, marginTop: 8 }} value={d.keyLabel} maxLength={200} onChange={e => set('keyLabel', e.target.value)} placeholder='Key label (optional), e.g. "production server"' />
            )}
          </div>
        )}

        {error && <div style={{ color: '#ef4444', marginBottom: 12, fontSize: 13 }}>{error}</div>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={() => void save()} disabled={saving}>{saving ? 'Saving…' : client ? 'Save' : 'Create client'}</button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// New key, shown once
// ---------------------------------------------------------------------------

function ShowKey({ clientName, newKey, onClose }: { clientName: string; newKey: NewApiKey; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(newKey.key); setCopied(true); } catch { setCopied(false); }
  };
  // Clicks stay here: when opened from the Keys panel, this sits inside that
  // panel's backdrop, whose click closes the panel.
  return (
    <div style={overlay} onClick={e => e.stopPropagation()}>
      <div style={modal(560)}>
        <h2 style={{ margin: '0 0 6px', fontSize: 18 }}>New API key for {clientName}</h2>
        <p style={{ margin: '0 0 14px', fontSize: 13, color: '#b45309' }}>
          Copy it now. This is the only time the full key is shown; only its prefix is stored.
        </p>
        <div style={{ display: 'flex', gap: 8, alignItems: 'stretch' }}>
          <code style={{ ...mono, flex: 1, background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: 6, padding: '10px 12px', wordBreak: 'break-all', userSelect: 'all' }}>{newKey.key}</code>
          <button className="btn btn-secondary" onClick={() => void copy()}>{copied ? 'Copied' : 'Copy'}</button>
        </div>
        {newKey.record.label && <div style={hint}>Label: {newKey.record.label}</div>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 18 }}>
          <button className="btn btn-primary" onClick={onClose}>I've stored the key</button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Keys of one client
// ---------------------------------------------------------------------------

function KeysPanel({ client, onClose, onChanged }: { client: EnrichmentClient; onClose: () => void; onChanged: () => void }) {
  const { getToken } = useAuth();
  const [keys, setKeys] = useState<ApiKeyRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [shown, setShown] = useState<NewApiKey | null>(null);

  const load = useCallback(async () => {
    setError('');
    try {
      const token = await getToken();
      if (!token) { setError('Your session expired. Please sign in again.'); return; }
      setKeys(await listKeys(token, client.id));
    } catch (e: any) {
      setError(errorMessage(e, 'Failed to load keys'));
    } finally { setLoading(false); }
  }, [getToken, client.id]);

  useEffect(() => { void load(); }, [load]);

  const issue = async () => {
    setBusy('issue'); setError('');
    try {
      const token = await getToken();
      if (!token) { return; }
      setShown(await issueKey(token, client.id, newLabel.trim() || undefined));
      setNewLabel('');
      await load();
      onChanged();
    } catch (e: any) {
      setError(errorMessage(e, 'Could not issue a key'));
    } finally { setBusy(null); }
  };

  const revoke = async (k: ApiKeyRecord) => {
    if (!window.confirm(`Revoke key ${k.key_prefix}…${k.label ? ` (${k.label})` : ''}? It stops working immediately and can't be restored.`)) { return; }
    setBusy(k.id); setError('');
    try {
      const token = await getToken();
      if (!token) { return; }
      await revokeKey(token, client.id, k.id);
      await load();
      onChanged();
    } catch (e: any) {
      setError(errorMessage(e, 'Could not revoke the key'));
    } finally { setBusy(null); }
  };

  return (
    <div style={overlay} onClick={onClose}>
      <div style={modal(820)} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 14 }}>
          <h2 style={{ margin: 0, fontSize: 18 }}>API keys: {client.name}</h2>
          <div style={{ flex: 1 }} />
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>

        <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
          <input style={{ ...input, flex: 1 }} value={newLabel} maxLength={200} onChange={e => setNewLabel(e.target.value)} placeholder='Label for a new key (optional), e.g. "production server"' />
          <button className="btn btn-primary" onClick={() => void issue()} disabled={busy === 'issue'}>{busy === 'issue' ? 'Issuing…' : 'Issue new key'}</button>
        </div>

        {error && <div style={{ color: '#ef4444', marginBottom: 12, fontSize: 13 }}>{error}</div>}
        {loading ? <p style={{ color: '#9ca3af' }}>Loading…</p> : (
          <div style={{ overflowX: 'auto', border: '1px solid #e5e7eb', borderRadius: 10 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>{['Status', 'Key', 'Label', 'Created', 'Last used', ''].map(h => <th key={h} style={th}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {keys.map(k => (
                  <tr key={k.id}>
                    <td style={td}>{k.revoked_at ? <Badge color="#6b7280">REVOKED</Badge> : <Badge color="#22c55e">ACTIVE</Badge>}</td>
                    <td style={{ ...td, ...mono }}>{k.key_prefix}…</td>
                    <td style={td}>{k.label || <span style={{ color: '#9ca3af' }}>—</span>}</td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>{fmtDate(k.created_at)}</td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }} title={fmtDate(k.last_used_at)}>{ago(k.last_used_at)}</td>
                    <td style={{ ...td, whiteSpace: 'nowrap', textAlign: 'right' }}>
                      {k.revoked_at
                        ? <span style={{ fontSize: 12, color: '#6b7280' }}>revoked {ago(k.revoked_at)}</span>
                        : <button className="btn btn-secondary" style={{ ...small, color: '#b91c1c' }} disabled={busy === k.id} onClick={() => void revoke(k)}>Revoke</button>}
                    </td>
                  </tr>
                ))}
                {!keys.length && <tr><td style={td} colSpan={6}>No keys yet. Issue one above.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {shown && <ShowKey clientName={client.name} newKey={shown} onClose={() => setShown(null)} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Usage (billing record)
// ---------------------------------------------------------------------------

type Period = 7 | 30 | 90;
const PERIODS: Period[] = [7, 30, 90];

const isoDay = (d: Date) => d.toISOString().slice(0, 10);
/** Last `days` days including today (UTC, like the API's usage days). */
function periodRange(days: Period): { from: string; to: string } {
  const to = new Date();
  return { from: isoDay(new Date(to.getTime() - (days - 1) * 86_400_000)), to: isoDay(to) };
}
const fmtN = (n: number | null | undefined) => (n == null ? '—' : Number(n).toLocaleString());
const rate = (matches: number, records: number) => (records ? `${((matches / records) * 100).toFixed(1)}%` : '—');

/** Every day of the range, zero-filled (the API only returns days with usage). Newest first. */
function fillDays(days: UsageDay[], from: string, to: string): UsageDay[] {
  const byDay = new Map(days.map(d => [d.day.slice(0, 10), d]));
  const out: UsageDay[] = [];
  for (let t = Date.parse(to); t >= Date.parse(from); t -= 86_400_000) {
    const day = isoDay(new Date(t));
    out.push(byDay.get(day) ?? { day, requests: 0, records: 0, matches: 0, external_matches: 0 });
  }
  return out;
}

function UsagePanel({ client, initialPeriod, onClose }: { client: EnrichmentClient; initialPeriod: Period; onClose: () => void }) {
  const { getToken } = useAuth();
  const [period, setPeriod] = useState<Period>(initialPeriod);
  const [usage, setUsage] = useState<ClientUsage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { from, to } = periodRange(period);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError('');
    void (async () => {
      try {
        const token = await getToken();
        if (!token) { setError('Your session expired. Please sign in again.'); return; }
        const u = await getClientUsage(token, client.id, from, to);
        if (!cancelled) { setUsage(u); }
      } catch (e: any) {
        if (!cancelled) { setError(errorMessage(e, 'Failed to load usage')); }
      } finally { if (!cancelled) { setLoading(false); } }
    })();
    return () => { cancelled = true; };
  }, [getToken, client.id, from, to]);

  const days = usage ? fillDays(usage.days, from, to) : [];
  const peak = Math.max(1, ...days.map(d => d.records));
  const card: React.CSSProperties = { background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 14px', minWidth: 120 };

  return (
    <div style={overlay} onClick={onClose}>
      <div style={modal(820)} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
          <h2 style={{ margin: 0, fontSize: 18 }}>Usage: {client.name}</h2>
          <div style={{ flex: 1 }} />
          <select style={{ ...input, width: 130 }} value={period} onChange={e => setPeriod(Number(e.target.value) as Period)}>
            {PERIODS.map(p => <option key={p} value={p}>Last {p} days</option>)}
          </select>
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>

        {error && <div style={{ color: '#ef4444', marginBottom: 12, fontSize: 13 }}>{error}</div>}
        {loading ? <p style={{ color: '#9ca3af' }}>Loading…</p> : usage && (
          <>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
              {[['Requests', fmtN(usage.requests)], ['Records', fmtN(usage.records)], ['Matches (billable)', fmtN(usage.matches)],
                ['Live-provider matches', fmtN(usage.external_matches)], ['Match rate', rate(usage.matches, usage.records)]].map(([k, v]) => (
                <div key={k} style={card}><div style={{ ...label, marginBottom: 4 }}>{k}</div><div style={{ fontSize: 18, fontWeight: 700 }}>{v}</div></div>
              ))}
            </div>
            <div style={{ overflowX: 'auto', border: '1px solid #e5e7eb', borderRadius: 10, maxHeight: 420, overflowY: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>{['Day (UTC)', 'Requests', 'Records', 'Matches', 'Live matches', 'Match rate', ''].map(h =>
                    <th key={h} style={h === 'Day (UTC)' || h === '' ? { ...th, position: 'sticky', top: 0 } : { ...th, textAlign: 'right', position: 'sticky', top: 0 }}>{h}</th>)}</tr>
                </thead>
                <tbody>
                  {days.map(d => (
                    <tr key={d.day}>
                      <td style={{ ...td, whiteSpace: 'nowrap', color: d.records ? td.color : '#9ca3af' }}>{d.day}</td>
                      <td style={num}>{fmtN(d.requests)}</td>
                      <td style={num}>{fmtN(d.records)}</td>
                      <td style={num}>{fmtN(d.matches)}</td>
                      <td style={num}>{fmtN(d.external_matches)}</td>
                      <td style={num}>{rate(d.matches, d.records)}</td>
                      <td style={{ ...td, width: 160 }}>
                        <div style={{ height: 8, background: '#eef2ff', borderRadius: 4, overflow: 'hidden' }} title={`${fmtN(d.records)} records`}>
                          <div style={{ width: `${(d.records / peak) * 100}%`, height: '100%', background: '#6366f1' }} />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={hint}>Matches are billable; live-provider matches are the part answered by a paid live lookup.</div>
          </>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Board
// ---------------------------------------------------------------------------

export default function EnrichmentApiClients() {
  const { user, getToken } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';

  const [clients, setClients] = useState<EnrichmentClient[]>([]);
  const [usage, setUsage] = useState<Map<string, ClientUsage>>(new Map());
  const [usageError, setUsageError] = useState('');
  const [period, setPeriod] = useState<Period>(30);
  const [statusFilter, setStatusFilter] = useState<'' | ClientStatus>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Delete refused because the client has usage: offer to suspend instead.
  const [suspendInstead, setSuspendInstead] = useState<{ client: EnrichmentClient; message: string } | null>(null);
  const [editing, setEditing] = useState<EnrichmentClient | null | 'new'>(null);
  const [keysFor, setKeysFor] = useState<EnrichmentClient | null>(null);
  const [usageFor, setUsageFor] = useState<EnrichmentClient | null>(null);
  const [newKey, setNewKey] = useState<{ clientName: string; key: NewApiKey } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(''); setUsageError('');
    try {
      const token = await getToken();
      if (!token) { setError('Your session expired. Please sign in again.'); return; }
      const { from, to } = periodRange(period);
      // Usage is informational: a failure there must not hide the client list.
      const [list, u] = await Promise.all([
        listClients(token, statusFilter || undefined),
        getUsage(token, from, to).catch((e: any) => { setUsageError(errorMessage(e, 'Usage unavailable')); return null; }),
      ]);
      setClients(list);
      setUsage(new Map((u?.clients ?? []).map(c => [c.client_id, c])));
    } catch (e: any) {
      setError(errorMessage(e, 'Failed to load clients'));
    } finally { setLoading(false); }
  }, [getToken, statusFilter, period]);

  useEffect(() => {
    if (!isSuperAdmin) { return; }
    void load();
  }, [isSuperAdmin, load]);

  const setStatus = async (c: EnrichmentClient, next: ClientStatus) => {
    if (next === 'suspended' && !window.confirm(`Suspend ${c.name}? All of its API keys stop working until it's reactivated. Its usage history is kept.`)) { return; }
    setBusy(c.id); setError(''); setSuspendInstead(null);
    try {
      const token = await getToken();
      if (!token) { return; }
      await updateClient(token, c.id, { status: next });
      await load();
    } catch (e: any) {
      setError(errorMessage(e, 'Could not change the status'));
    } finally { setBusy(null); }
  };

  const remove = async (c: EnrichmentClient) => {
    if (!window.confirm(`Delete ${c.name}? Only possible for a client that never made a request; otherwise suspend it instead.`)) { return; }
    setBusy(c.id); setError(''); setSuspendInstead(null);
    try {
      const token = await getToken();
      if (!token) { return; }
      await deleteClient(token, c.id);
      await load();
    } catch (e: any) {
      if (e?.response?.data?.code === 'HAS_USAGE' && c.status === 'active') {
        setSuspendInstead({ client: c, message: 'it has usage history, which is the billing record and is kept. Suspend it instead: its keys stop working, and you can reactivate it any time.' });
      } else {
        setError(errorMessage(e, 'Could not delete the client'));
      }
    } finally { setBusy(null); }
  };

  if (!isSuperAdmin) {
    return <div className="page"><p style={{ color: '#9ca3af' }}>Super admin access required.</p></div>;
  }

  const active = clients.filter(c => c.status === 'active').length;
  const totals = clients.reduce((t, c) => {
    const u = usage.get(c.id);
    if (u) { t.requests += u.requests; t.records += u.records; t.matches += u.matches; t.external += u.external_matches; }
    return t;
  }, { requests: 0, records: 0, matches: 0, external: 0 });
  const card: React.CSSProperties = { background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '14px 16px', minWidth: 140, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' };
  const right = ['Rate / min', 'Active keys', 'Requests', 'Records', 'Matches', 'Live matches'];

  return (
    <div className="page" style={{ padding: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 6 }}>
        <h1 style={{ margin: 0, fontSize: 22, color: '#1f2937' }}>Enrichment API</h1>
        <div style={{ flex: 1 }} />
        <select style={{ ...input, width: 140 }} value={period} onChange={e => { setLoading(true); setPeriod(Number(e.target.value) as Period); }} title="Usage period">
          {PERIODS.map(p => <option key={p} value={p}>Last {p} days</option>)}
        </select>
        <select style={{ ...input, width: 150 }} value={statusFilter} onChange={e => { setLoading(true); setStatusFilter(e.target.value as '' | ClientStatus); }}>
          <option value="">All clients</option><option value="active">Active</option><option value="suspended">Suspended</option>
        </select>
        <button className="btn btn-secondary" onClick={() => void load()}>Refresh</button>
        <button className="btn btn-primary" onClick={() => setEditing('new')}>New client</button>
      </div>
      <p style={{ margin: '0 0 18px', fontSize: 13, color: '#6b7280' }}>
        Clients, API keys and usage of the BigSky Enrichment API (enrich.databody.app).
      </p>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 18 }}>
        {[['Clients', `${active} active / ${clients.length}`], ['Requests', fmtN(totals.requests)], ['Records', fmtN(totals.records)],
          ['Matches (billable)', fmtN(totals.matches)], ['Live-provider matches', fmtN(totals.external)], ['Match rate', rate(totals.matches, totals.records)]].map(([k, v]) => (
          <div key={k} style={card}>
            <div style={{ ...label, marginBottom: 6 }}>{k}{k !== 'Clients' ? ` · ${period}d` : ''}</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: '#1f2937' }}>{v}</div>
          </div>
        ))}
      </div>

      {error && <div style={{ color: '#ef4444', marginBottom: 12 }}>{error}</div>}
      {usageError && <div style={{ color: '#b45309', marginBottom: 12, fontSize: 13 }}>Usage could not be loaded: {usageError}</div>}
      {suspendInstead && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: 13 }}>
          <span><strong>{suspendInstead.client.name}</strong> can't be deleted: {suspendInstead.message}</span>
          <div style={{ flex: 1 }} />
          <button className="btn btn-primary" style={{ padding: '4px 12px', fontSize: 12 }} disabled={busy === suspendInstead.client.id}
            onClick={() => void setStatus(suspendInstead.client, 'suspended')}>Suspend instead</button>
          <button className="btn btn-secondary" style={{ padding: '4px 12px', fontSize: 12 }} onClick={() => setSuspendInstead(null)}>Dismiss</button>
        </div>
      )}
      {loading ? <p style={{ color: '#9ca3af' }}>Loading…</p> : (
        <div style={{ overflowX: 'auto', border: '1px solid #e5e7eb', borderRadius: 10, background: '#ffffff' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {['Status', 'Client', 'Scopes', 'Rate / min', 'Live lookup', 'MCP', 'Attributes', 'Active keys', 'Requests', 'Records', 'Matches', 'Live matches', ''].map(h =>
                  <th key={h} style={right.includes(h) ? { ...th, textAlign: 'right' } : th} title={right.slice(2).includes(h) ? `Last ${period} days` : undefined}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {clients.map(c => {
                const u = usage.get(c.id);
                const suspended = c.status === 'suspended';
                return (
                  <tr key={c.id}>
                    <td style={td}><Badge color={suspended ? '#f59e0b' : '#22c55e'}>{suspended ? 'SUSPENDED' : 'ACTIVE'}</Badge></td>
                    <td style={{ ...td, minWidth: 240 }}>
                      <div style={{ fontWeight: 600 }}>{c.name}</div>
                      <div style={{ fontSize: 11, color: '#9ca3af', ...mono, whiteSpace: 'nowrap' }}>{c.id}</div>
                      <div style={{ fontSize: 11, color: '#9ca3af', whiteSpace: 'nowrap' }}>created {fmtDate(c.created_at)}</div>
                    </td>
                    <td style={td}>{c.scopes.map(s => <Badge key={s} color={s === 'admin' ? '#ef4444' : '#3b82f6'}>{s}</Badge>)}</td>
                    <td style={num}>{c.rate_limit_per_minute.toLocaleString()}</td>
                    <td style={td}>{c.live_lookup ? <Badge color="#8b5cf6">ON</Badge> : <span style={{ color: '#9ca3af' }}>off</span>}</td>
                    <td style={td}>{c.mcp_enabled ? <Badge color="#0ea5e9">ON</Badge> : <span style={{ color: '#9ca3af' }}>off</span>}</td>
                    <td style={td} title={c.allowed_attributes?.join(', ') ?? 'All attributes'}>{c.allowed_attributes ? `${c.allowed_attributes.length} selected` : 'All'}</td>
                    <td style={num}>{c.active_keys}</td>
                    <td style={num}>{fmtN(u?.requests ?? 0)}</td>
                    <td style={num}>{fmtN(u?.records ?? 0)}</td>
                    <td style={num}>{fmtN(u?.matches ?? 0)}</td>
                    <td style={num}>{fmtN(u?.external_matches ?? 0)}</td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>
                      <button className="btn btn-secondary" style={small} onClick={() => setUsageFor(c)}>Usage</button>
                      <button className="btn btn-secondary" style={small} onClick={() => setKeysFor(c)}>Keys</button>
                      <button className="btn btn-secondary" style={small} onClick={() => setEditing(c)}>Edit</button>
                      {suspended
                        ? <button className="btn btn-secondary" style={{ ...small, color: '#15803d' }} disabled={busy === c.id} onClick={() => void setStatus(c, 'active')}>Reactivate</button>
                        : <button className="btn btn-secondary" style={{ ...small, color: '#b45309' }} disabled={busy === c.id} onClick={() => void setStatus(c, 'suspended')}>Suspend</button>}
                      <button className="btn btn-secondary" style={{ ...small, color: '#b91c1c' }} disabled={busy === c.id} onClick={() => void remove(c)}
                        title="Only for a client that never made a request">Delete</button>
                    </td>
                  </tr>
                );
              })}
              {!clients.length && <tr><td style={td} colSpan={12}>No clients{statusFilter ? ` with status ${statusFilter}` : ''}. Click New client.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <ClientEditor
          client={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={(k) => { setEditing(null); if (k) { setNewKey(k); } void load(); }}
        />
      )}
      {keysFor && <KeysPanel client={keysFor} onClose={() => setKeysFor(null)} onChanged={() => void load()} />}
      {usageFor && <UsagePanel client={usageFor} initialPeriod={period} onClose={() => setUsageFor(null)} />}
      {newKey && <ShowKey clientName={newKey.clientName} newKey={newKey.key} onClose={() => setNewKey(null)} />}
    </div>
  );
}
